import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Landmark, ShieldCheck, AlertTriangle, TrendingUp } from "lucide-react";

export default function TrustAccountsPage() {
  const { propertyId } = useProperty();
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [report, setReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({
    name: "", account_type: "operating", bank_name: "",
    account_number_tail: "", routing_number_tail: "",
    is_default_for_type: false, opening_balance: 0, notes: "",
  });

  const load = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/trust/accounts?property_id=${propertyId}`);
      setAccounts(data.items || []);
      setSummary(data.summary || null);
    } catch (e) { toast.error("Failed to load accounts"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [propertyId]);

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      await api.post("/admin/trust/accounts", {
        property_id: propertyId,
        ...form,
        opening_balance: parseFloat(form.opening_balance || 0),
      });
      toast.success("Account created");
      setDialog(false);
      setForm({ name: "", account_type: "operating", bank_name: "", account_number_tail: "", routing_number_tail: "", is_default_for_type: false, opening_balance: 0, notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runReport = async () => {
    try {
      const { data } = await api.get(`/admin/trust/report?property_id=${propertyId}&period=${reportPeriod}`);
      setReport(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="trust-accounts-page">
      <PageHeader
        eyebrow="Financials"
        title="Trust & Operating Accounts"
        subtitle="Keep client trust funds (security deposits, prepaid rent) segregated from your operating cash — state PM law compliance."
        actions={
          <Button onClick={() => setDialog(true)} className="gap-2" data-testid="new-account-btn">
            <Plus size={14} /> New account
          </Button>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Landmark size={12} /> Operating
            </div>
            <div className="text-2xl font-semibold mt-1" data-testid="op-count">{summary.operating_count}</div>
            <div className="text-xs text-muted-foreground mt-1">${(summary.operating_total_balance || 0).toLocaleString()}</div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <ShieldCheck size={12} /> Trust
            </div>
            <div className="text-2xl font-semibold mt-1" data-testid="trust-count">{summary.trust_count}</div>
            <div className="text-xs text-muted-foreground mt-1">${(summary.trust_total_balance || 0).toLocaleString()}</div>
          </Card>
          <Card className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Deposits Obligated</div>
            <div className="text-2xl font-semibold mt-1 text-amber-500">${(summary.trust_deposits_held || 0).toLocaleString()}</div>
          </Card>
          <Card className={`p-4 border-white/10 bg-card ${!summary.trust_healthy ? "border-rose-500/40" : ""}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              {summary.trust_healthy ? <ShieldCheck size={12} /> : <AlertTriangle size={12} className="text-rose-500" />}
              Trust Health
            </div>
            <div className={`text-2xl font-semibold mt-1 ${summary.trust_healthy ? "text-emerald-500" : "text-rose-400"}`} data-testid="trust-health">
              {summary.trust_healthy ? "Healthy" : "UNDER"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              variance: ${(summary.trust_variance || 0).toLocaleString()}
            </div>
          </Card>
        </div>
      )}

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">Accounts</div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead className="text-right">Opening</TableHead>
              <TableHead className="text-right">Inflows</TableHead>
              <TableHead className="text-right">Outflows</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Trust Health</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.length === 0 && (
              <TableRow className="border-white/5"><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm" data-testid="accounts-empty">
                No accounts yet. Create one operating + one trust account minimum.
              </TableCell></TableRow>
            )}
            {accounts.map((a) => (
              <TableRow key={a.id} className="border-white/5" data-testid={`account-row-${a.id}`}>
                <TableCell className="font-medium">
                  {a.name}
                  {a.is_default_for_type && <Badge variant="outline" className="ml-2 text-[10px]">default</Badge>}
                </TableCell>
                <TableCell>
                  <Badge variant={a.account_type === "trust" ? "default" : "secondary"}>
                    {a.account_type}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {a.bank_name} {a.account_number_tail && `··${a.account_number_tail}`}
                </TableCell>
                <TableCell className="text-right font-mono">${(a.opening_balance || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-emerald-500">${(a.inflows_total || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-rose-400">${(a.outflows_total || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono">${(a.computed_balance || 0).toLocaleString()}</TableCell>
                <TableCell>
                  {a.account_type === "trust" ? (
                    a.trust_healthy ? (
                      <Badge className="bg-emerald-600" data-testid={`healthy-${a.id}`}>OK · deposits ${a.deposits_held?.toLocaleString()}</Badge>
                    ) : (
                      <Badge variant="destructive" data-testid={`under-${a.id}`}>UNDER by ${Math.abs(a.trust_variance || 0).toLocaleString()}</Badge>
                    )
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 border-white/10 bg-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-medium">Monthly Trust Reconciliation Report</div>
            <div className="text-xs text-muted-foreground">Auditor-ready summary per trust account per period.</div>
          </div>
          <div className="flex gap-2">
            <Input type="month" value={reportPeriod} onChange={(e) => setReportPeriod(e.target.value)} className="w-40" data-testid="report-period" />
            <Button onClick={runReport} data-testid="run-report">Generate</Button>
          </div>
        </div>
        {report && (
          <div className="space-y-3" data-testid="report-output">
            {report.accounts.map((a) => (
              <div key={a.account_id} className={`border rounded p-3 ${a.trust_healthy ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}>
                <div className="flex justify-between items-center mb-2">
                  <div className="font-medium">{a.account_name}</div>
                  <Badge variant={a.trust_healthy ? "default" : "destructive"}>
                    {a.trust_healthy ? "Healthy" : "UNDER"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Opening</span> <div className="font-mono">${a.opening_balance.toLocaleString()}</div></div>
                  <div><span className="text-muted-foreground">Inflows</span> <div className="font-mono text-emerald-500">+${a.total_inflows.toLocaleString()}</div></div>
                  <div><span className="text-muted-foreground">Outflows</span> <div className="font-mono text-rose-400">-${a.total_outflows.toLocaleString()}</div></div>
                  <div><span className="text-muted-foreground">Closing</span> <div className="font-mono font-semibold">${a.closing_balance.toLocaleString()}</div></div>
                  <div><span className="text-muted-foreground">Deposits obligated</span> <div className="font-mono">${a.deposits_held.toLocaleString()}</div></div>
                </div>
              </div>
            ))}
            {report.accounts.length === 0 && (
              <div className="text-sm text-muted-foreground">No trust accounts configured for this property yet.</div>
            )}
          </div>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg" data-testid="account-dialog">
          <DialogHeader><DialogTitle>New bank account</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2 col-span-2"><Label>Nickname</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="acc-name" /></div>
            <div className="space-y-2"><Label>Type</Label>
              <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                <SelectTrigger data-testid="acc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operating">Operating</SelectItem>
                  <SelectItem value="trust">Trust (client funds)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Opening balance</Label>
              <Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="acc-opening" /></div>
            <div className="space-y-2"><Label>Bank name</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="acc-bank" /></div>
            <div className="space-y-2"><Label>Acct # (last 4)</Label>
              <Input value={form.account_number_tail} onChange={(e) => setForm({ ...form, account_number_tail: e.target.value })} data-testid="acc-tail" /></div>
            <div className="space-y-2 col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.is_default_for_type} onChange={(e) => setForm({ ...form, is_default_for_type: e.target.checked })} data-testid="acc-default" />
              <Label>Set as default for this account type</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={create} data-testid="acc-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
